// Cave84 — <Money> — single source of truth for rendering currency values.
// Privacy First: when the global privacy switch is hidden (default), no
// currency value ever reaches the DOM — a fixed placeholder renders instead.
// Do NOT format currency anywhere else in the app; route every € / cost /
// market-value / target-price display through this component.
//
// Scope note (product decision): Inventory and the Wine Analysis market-price
// displays (Prices tab + sidebar Market card) are exempt from masking —
// prices there are always visible via `forceVisible`. Every other screen
// (Dashboard, Investment, Wishlist, Price Review, Special Occasions, Cellars,
// MWC/CellarTracker import) stays toggle-controlled.

import { useMemo } from 'react';
import type { Currency } from '@cave84/shared-types';
import { usePrivacy } from '@/hooks/usePrivacy';

export type MoneyIntent = 'neutral' | 'positive' | 'negative';

// Mirrors tailwind.config.ts `colors.pos` / `colors.neg` and the BN.pos /
// BN.neg design tokens used across inline-styled pages. Kept as a literal
// here so Money never depends on which styling system the caller uses.
const INTENT_COLOR: Record<Exclude<MoneyIntent, 'neutral'>, string> = {
  positive: '#8FB57C',
  negative: '#C46F50',
};

// Fixed-length placeholder — intentionally NOT derived from the real value's
// digit count, so hiding a value never leaks its magnitude via width.
const HIDDEN_PLACEHOLDER = '•••••';

export interface MoneyProps {
  value: number;
  currency: Currency;
  /** Show a leading "+" for positive values (e.g. gain/loss deltas). */
  sign?: boolean;
  /** Colors the value when revealed. 'neutral' leaves color to the caller. */
  intent?: MoneyIntent;
  className?: string;
  style?: React.CSSProperties;
  /** Reserved inline width (ch units) so toggling hidden/visible never shifts layout. */
  minWidthCh?: number;
  /** Decimal precision. Default 0 (whole currency units); pass 2 for per-bottle cent-level prices. */
  maximumFractionDigits?: number;
  /**
   * Escape hatch for screens exempted from Privacy First (currently: Inventory).
   * Bypasses the global privacy toggle and always renders the real value.
   * Use sparingly — the default (false) is what "single source of truth" means.
   */
  forceVisible?: boolean;
}

export function Money({
  value,
  currency,
  sign = false,
  intent = 'neutral',
  className,
  style,
  minWidthCh = 10,
  maximumFractionDigits = 0,
  forceVisible = false,
}: MoneyProps) {
  const { hidden: globalHidden } = usePrivacy();
  const hidden = forceVisible ? false : globalHidden;

  // Only formatted (and only exists) when actually rendered — never computed
  // and discarded on the hidden path.
  const formatted = useMemo(() => {
    if (hidden) return null;
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency,
      maximumFractionDigits,
      minimumFractionDigits: maximumFractionDigits,
      signDisplay: sign ? 'exceptZero' : 'auto',
    }).format(value);
  }, [hidden, value, currency, sign, maximumFractionDigits]);

  const color = intent === 'neutral' ? undefined : INTENT_COLOR[intent];

  return (
    <span
      className={className}
      dir="ltr"
      aria-label={hidden ? 'Hidden value' : undefined}
      style={{
        display: 'inline-block',
        minWidth: `${minWidthCh}ch`,
        fontVariantNumeric: 'tabular-nums',
        color,
        ...style,
      }}
    >
      {hidden ? HIDDEN_PLACEHOLDER : formatted}
    </span>
  );
}
